import React, { useState } from "react";
import {
  CheckCircle, XCircle, Eye, Send, RotateCcw, RefreshCw, GitBranch, GitCompareArrows, Share2, ArrowRight, Mail, MessageSquare,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { formatDate, formatDateTime } from "../../lib/contractUtils";

export function WorkflowStepper({ workflow, contract, user, canReview, canApprove, openActionDialog, onCancelWorkflow, onResubmit, onSubmitForReview, onShareDraft, onAdvanceToApproval, onRecordExternalFeedback, onViewChanges }) {
  if (!workflow) {
    return (
      <div className="text-center py-20 text-slate-400">
        <GitBranch size={48} className="mx-auto mb-4 opacity-50" />
        <p>No workflow created</p>
        {contract.status === "draft" && (
          <Button onClick={onSubmitForReview} variant="link" className="text-cyan-600 mt-2">Submit for review</Button>
        )}
      </div>
    );
  }

  const isCreator = workflow.created_by === user?.id;
  const isSupplierReview = workflow.stage === "supplier_review";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium text-lg">Governed Approval Workflow</span>
          <p className="text-sm text-slate-500 mt-0.5">
            Review → Supplier Review (optional) → Approval
            {workflow.due_date && ` · Due ${formatDate(workflow.due_date)}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Badge className={`rounded-sm ${
            workflow.stage === "review" ? "bg-amber-100 text-amber-700" :
            workflow.stage === "revision" ? "bg-orange-100 text-orange-700" :
            workflow.stage === "supplier_review" ? "bg-indigo-100 text-indigo-700" :
            workflow.stage === "approval" ? "bg-blue-100 text-blue-700" :
            workflow.stage === "completed" ? "bg-emerald-100 text-emerald-700" :
            "bg-slate-100 text-slate-600"
          }`} data-testid="workflow-stage-badge">
            {workflow.stage === "review" ? "Under Review" :
             workflow.stage === "revision" ? "Revision Required" :
             workflow.stage === "supplier_review" ? "Supplier Review" :
             workflow.stage === "approval" ? "Pending Approval" :
             workflow.stage === "completed" ? "Completed" : "Cancelled"}
          </Badge>
          <Badge className={`rounded-sm ${
            workflow.status === "approved" ? "bg-emerald-100 text-emerald-700" :
            workflow.status === "rejected" ? "bg-red-100 text-red-700" :
            "bg-blue-100 text-blue-700"
          }`} data-testid="workflow-status-badge">
            {workflow.status}
          </Badge>
        </div>
      </div>

      {/* Three-stage Visual Stepper */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StageCard
          title="Review"
          icon={<Eye size={16} className="text-amber-600" />}
          steps={workflow.review_steps}
          stage={workflow.stage}
          activeStage="review"
          pendingLabel="Awaiting review"
          activeColor="amber"
        />
        <SupplierReviewCard
          stage={workflow.stage}
          contract={contract}
          isCreator={isCreator}
          onShareDraft={onShareDraft}
          onAdvance={onAdvanceToApproval}
        />
        <StageCard
          title="Approval"
          icon={<CheckCircle size={16} className="text-blue-600" />}
          steps={workflow.approval_steps}
          stage={workflow.stage}
          activeStage="approval"
          pendingLabel="Awaiting approval"
          activeColor="blue"
        />
      </div>

      {/* External Reviewers Section */}
      {workflow.external_reviewers?.length > 0 && (
        <ExternalReviewersCard
          externalReviewers={workflow.external_reviewers}
          isCreator={isCreator}
          onRecordFeedback={onRecordExternalFeedback}
        />
      )}

      {/* Revision History */}
      {workflow.revisions?.length > 0 && (
        <div className="border-t pt-4">
          <p className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-2">
            <RefreshCw size={14} /> Revision History ({workflow.revisions.length})
          </p>
          <div className="space-y-2">
            {workflow.revisions.map((rev, i) => (
              <div key={i} className="text-xs p-2 bg-orange-50 dark:bg-orange-900/10 rounded border border-orange-200">
                <span className="font-medium">Revision #{rev.revision_number}</span> — Sent back by {rev.sent_back_by_name}
                {rev.reason && <span className="italic"> — "{rev.reason}"</span>}
                <span className="text-slate-400 ml-2">{formatDateTime(rev.created_at)}</span>
                {rev.resubmitted_at && <span className="text-emerald-600 ml-2">Resubmitted {formatDateTime(rev.resubmitted_at)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Workflow Actions */}
      <div className="flex gap-2 pt-2 border-t flex-wrap">
        {(canReview || canApprove) && onViewChanges && (
          <Button onClick={onViewChanges} variant="outline" className="gap-2 rounded-sm text-indigo-600 border-indigo-200 hover:bg-indigo-50" data-testid="wf-view-changes-btn">
            <GitCompareArrows size={16} /> View Changes
          </Button>
        )}
        {canReview && (
          <>
            <Button onClick={() => openActionDialog("review-approve")} className="gap-2 rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="wf-tab-review-approve">
              <CheckCircle size={16} /> Approve Review
            </Button>
            <Button onClick={() => openActionDialog("send-back")} variant="outline" className="gap-2 rounded-sm text-orange-600 border-orange-200 hover:bg-orange-50" data-testid="wf-tab-send-back">
              <RotateCcw size={16} /> Send Back
            </Button>
          </>
        )}
        {canApprove && (
          <>
            <Button onClick={() => openActionDialog("approve")} className="gap-2 rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="wf-tab-approve">
              <CheckCircle size={16} /> Approve
            </Button>
            <Button onClick={() => openActionDialog("reject")} variant="outline" className="gap-2 rounded-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="wf-tab-reject">
              <XCircle size={16} /> Reject
            </Button>
          </>
        )}
        {workflow.status === "pending" && isCreator && workflow.stage !== "revision" && workflow.stage !== "supplier_review" && (
          <Button onClick={onCancelWorkflow} variant="outline" className="gap-2 rounded-sm" data-testid="wf-tab-cancel">
            <XCircle size={16} /> Recall
          </Button>
        )}
        {workflow.stage === "revision" && isCreator && (
          <Button onClick={onResubmit} variant="outline" className="gap-2 rounded-sm text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="wf-tab-resubmit">
            <Send size={16} /> Resubmit for Review
          </Button>
        )}
      </div>
    </div>
  );
}

function SupplierReviewCard({ stage, contract, isCreator, onShareDraft, onAdvance }) {
  const stageOrder = ["review", "revision", "supplier_review", "approval", "completed"];
  const currentIdx = stageOrder.indexOf(stage);
  const supplierIdx = stageOrder.indexOf("supplier_review");
  const isActive = stage === "supplier_review";
  const isComplete = currentIdx > supplierIdx;
  const draftShared = !!contract?.draft_shared_at;

  return (
    <div className={`p-4 rounded-lg border-2 ${
      isActive ? "border-indigo-300 bg-indigo-50/30" :
      isComplete ? "border-emerald-300 bg-emerald-50/30" :
      "border-slate-200 bg-slate-50/30"
    }`} data-testid="supplier-review-card">
      <div className="flex items-center gap-2 mb-3">
        <Share2 size={16} className={isActive ? "text-indigo-600" : isComplete ? "text-emerald-600" : "text-slate-400"} />
        <span className="font-semibold text-sm uppercase tracking-wider text-slate-600">Supplier Review</span>
        <span className="text-xs text-slate-400 ml-auto">(Optional)</span>
        {isComplete && <CheckCircle size={16} className="text-emerald-600" />}
      </div>

      {isActive ? (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Share the draft with the supplier for feedback before final approval. This step is optional — you can skip directly to approval.
          </p>
          {draftShared && (
            <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 p-2 rounded border border-emerald-200">
              <CheckCircle size={14} />
              <span>Draft shared with {contract.draft_shared_to} on {formatDate(contract.draft_shared_at)}</span>
            </div>
          )}
          {isCreator && (
            <div className="flex gap-2 pt-1">
              <Button onClick={onShareDraft} variant="outline" size="sm" className="gap-1.5 rounded-sm text-indigo-600 border-indigo-200 hover:bg-indigo-50" data-testid="wf-share-draft-btn">
                <Share2 size={14} /> Share Draft
              </Button>
              <Button onClick={onAdvance} size="sm" className="gap-1.5 rounded-sm" data-testid="wf-advance-approval-btn">
                <ArrowRight size={14} /> Proceed to Approval
              </Button>
            </div>
          )}
        </div>
      ) : isComplete ? (
        <div className="space-y-2">
          {draftShared ? (
            <div className="flex items-center gap-2 text-xs text-emerald-600">
              <CheckCircle size={14} />
              <span>Draft shared with {contract.draft_shared_to}</span>
            </div>
          ) : (
            <p className="text-xs text-slate-400">Skipped — proceeded to approval</p>
          )}
        </div>
      ) : (
        <p className="text-xs text-slate-400">Pending — waiting for review stage to complete</p>
      )}
    </div>
  );
}

function StageCard({ title, icon, steps, stage, activeStage, pendingLabel, activeColor }) {
  const allApproved = steps?.every(s => s.status === "approved");
  const isActive = stage === activeStage;

  return (
    <div className={`p-4 rounded-lg border-2 ${
      isActive ? `border-${activeColor}-300 bg-${activeColor}-50/30` :
      allApproved ? "border-emerald-300 bg-emerald-50/30" :
      "border-slate-200 bg-slate-50/30"
    }`}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <span className="font-semibold text-sm uppercase tracking-wider text-slate-600">{title}</span>
        {allApproved && <CheckCircle size={16} className="text-emerald-600 ml-auto" />}
      </div>
      <div className="space-y-3">
        {steps?.map((step, index) => (
          <div key={index} className="flex gap-3" data-testid={`${activeStage === "review" ? "review" : "approval"}-step-${index}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
              step.status === "approved" ? "bg-emerald-100 border-emerald-400" :
              step.status === "rejected" ? "bg-red-100 border-red-400" :
              (isActive && step.status === "pending") ? `bg-${activeColor}-100 border-${activeColor}-400` :
              "bg-slate-50 border-slate-200"
            }`}>
              {step.status === "approved" ? <CheckCircle size={12} className="text-emerald-600" /> :
               step.status === "rejected" ? <XCircle size={12} className="text-red-600" /> :
               <span className="text-xs font-semibold text-slate-400">{index + 1}</span>}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800">{step.user_name}</p>
              <p className={`text-xs ${
                step.status === "approved" ? "text-emerald-600" :
                step.status === "rejected" ? "text-red-600" : "text-slate-400"
              }`}>
                {step.status === "pending" ? pendingLabel :
                 `${step.status}${step.decided_at ? " on " + formatDateTime(step.decided_at) : ""}`}
              </p>
              {step.comment && <p className="text-xs text-slate-500 italic mt-0.5">"{step.comment}"</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExternalReviewersCard({ externalReviewers, isCreator, onRecordFeedback }) {
  const [feedbackForm, setFeedbackForm] = useState(null); // reviewer id
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackAction, setFeedbackAction] = useState("approve");

  const handleSubmitFeedback = (reviewerId) => {
    if (!feedbackText.trim()) return;
    onRecordFeedback(reviewerId, { action: feedbackAction, feedback: feedbackText });
    setFeedbackForm(null);
    setFeedbackText("");
    setFeedbackAction("approve");
  };

  const allDone = externalReviewers.every(r => r.status === "feedback_received");

  return (
    <div className={`p-4 rounded-lg border-2 ${allDone ? "border-emerald-300 bg-emerald-50/30" : "border-violet-300 bg-violet-50/30"}`} data-testid="external-reviewers-card">
      <div className="flex items-center gap-2 mb-3">
        <Mail size={16} className="text-violet-600" />
        <span className="font-semibold text-sm uppercase tracking-wider text-slate-600">External Reviewers</span>
        <Badge className="rounded-sm text-[9px] bg-violet-100 text-violet-600 ml-auto">via Email</Badge>
        {allDone && <CheckCircle size={16} className="text-emerald-600" />}
      </div>
      <p className="text-xs text-slate-500 mb-3">
        These reviewers are outside the system. An email was sent requesting their feedback. The workflow creator records their response manually.
      </p>
      <div className="space-y-3">
        {externalReviewers.map((er) => (
          <div key={er.id} className="border rounded-sm p-3 bg-white/60" data-testid={`ext-reviewer-entry-${er.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
                  er.status === "feedback_received" ? "bg-emerald-100 border-emerald-400" : "bg-violet-100 border-violet-400"
                }`}>
                  {er.status === "feedback_received" ? <CheckCircle size={12} className="text-emerald-600" /> : <Mail size={12} className="text-violet-600" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">{er.name}</p>
                  <p className="text-xs text-slate-400">{er.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {er.status === "feedback_received" ? (
                  <Badge className={`rounded-sm text-[10px] ${
                    er.feedback_action === "approve" ? "bg-emerald-100 text-emerald-700" :
                    er.feedback_action === "reject" ? "bg-red-100 text-red-700" :
                    "bg-amber-100 text-amber-700"
                  }`}>{er.feedback_action === "request_changes" ? "Changes Requested" : er.feedback_action}</Badge>
                ) : (
                  <Badge className="rounded-sm text-[10px] bg-slate-100 text-slate-500">Awaiting response</Badge>
                )}
              </div>
            </div>

            {er.status === "feedback_received" && er.feedback && (
              <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-600 flex items-start gap-1.5">
                <MessageSquare size={12} className="mt-0.5 flex-shrink-0 text-slate-400" />
                <span>{er.feedback}</span>
              </div>
            )}

            {er.status === "pending" && isCreator && feedbackForm !== er.id && (
              <Button variant="outline" size="sm" className="mt-2 h-7 text-xs rounded-sm gap-1.5" onClick={() => setFeedbackForm(er.id)} data-testid={`record-feedback-btn-${er.id}`}>
                <MessageSquare size={12} /> Record Feedback
              </Button>
            )}

            {feedbackForm === er.id && (
              <div className="mt-3 p-3 border rounded-sm bg-slate-50/70 space-y-2" data-testid={`feedback-form-${er.id}`}>
                <div className="flex gap-2 items-center">
                  <span className="text-xs font-medium text-slate-600 whitespace-nowrap">Decision:</span>
                  <Select value={feedbackAction} onValueChange={setFeedbackAction}>
                    <SelectTrigger className="h-7 text-xs rounded-sm w-44" data-testid="ext-feedback-action"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approve">Approve</SelectItem>
                      <SelectItem value="reject">Reject</SelectItem>
                      <SelectItem value="request_changes">Request Changes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  placeholder="Paste or summarize the reviewer's feedback..."
                  value={feedbackText}
                  onChange={e => setFeedbackText(e.target.value)}
                  rows={2}
                  className="rounded-sm text-xs"
                  data-testid="ext-feedback-text"
                />
                <div className="flex gap-2">
                  <Button size="sm" className="h-7 text-xs rounded-sm gap-1" onClick={() => handleSubmitFeedback(er.id)} disabled={!feedbackText.trim()} data-testid="submit-ext-feedback-btn">
                    <CheckCircle size={12} /> Save
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs rounded-sm" onClick={() => { setFeedbackForm(null); setFeedbackText(""); }}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
